import { useEffect, useRef } from 'react';

export type VisibilityStateValue = DocumentVisibilityState;

type CallbackType = () => void | Promise<void>;

type NavbarClickCallbackType = (type: string) => void | Promise<void>;

export type UseWebviewReactivatedOptions = {
    /**
     * 웹뷰 활성화 감지 콜백함수
     */
    onResume?: CallbackType;

    /**
     * 웹뷰 비활성(멈춤) 감지 콜백함수
     */
    onPause?: CallbackType;

    /**
     * 웹뷰 back-> fore 감지 콜백함수
     */
    onReactivated?: CallbackType;

    /**
     * 하단 네비바 클릭 이벤트 콜백 함수
     */
    onNavbarClick?: NavbarClickCallbackType;
};

/**
 * 웹뷰 활성화 감지 훅 함수
 * background -> foreground: visibilitychange
 * 상위웹뷰가 닫히고 이전 웹뷰가 활성화 되었을때 감지: native:webviewResume
 */
const useWebviewReactivated = (opts: UseWebviewReactivatedOptions) => {
    const prevRef = useRef<VisibilityStateValue>(document.visibilityState);

    const invoke = async (callback?: CallbackType) => {
        if (!callback) {
            return;
        }

        await callback();
    };

    //back -> fore 감지 처리
    useEffect(() => {
        const handleVisibilityChange = async () => {
            console.log('# start visibilitychange event');
            const prev = prevRef.current;
            const current = document.visibilityState;

            if (prev === 'hidden' && current === 'visible') {
                await invoke(opts.onReactivated);
            }

            prevRef.current = current;
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    //웹뷰 활성화 처리
    useEffect(() => {
        const handleWebviewResume = async (event: Event) => {
            console.log('# start webview:resume event', event);

            const customEvent = event as CustomEvent;
            const { context } = customEvent.detail;

            console.log(`# webview:resume context=${context}`);

            if (!context) {
                await invoke(opts.onResume);
                return;
            }

            const { source, id } = context;

            if (source === 'navbar_click' && id && opts.onNavbarClick) {
                await opts.onNavbarClick(id);
            } else {
                await invoke(opts.onResume);
            }
        };

        window.addEventListener('webview:resume', handleWebviewResume);
        return () => {
            window.removeEventListener('webview:resume', handleWebviewResume);
        };
    }, []);

    //웹뷰 비활성화 감지 처리
    useEffect(() => {
        const handleWebviewPause = async () => {
            console.log('# start native:webviewPause event');
            await invoke(opts.onPause);
        };

        window.addEventListener('webview:pause', handleWebviewPause);
        return () => {
            window.removeEventListener('webview:pause', handleWebviewPause);
        };
    }, []);

    // /**
    //  * 하단 네비바 클릭 이벤트 처리
    //  */
    // useEffect(() => {
    //     if (channel !== Channel.MB) {
    //         return;
    //     }

    //     let mounted = true;

    //     const run = async () => {
    //         const context = await getNavigationContext();

    //         console.log(`# useEffect native:webviewResume context=${context}`);

    //         if (!mounted || !context) {
    //             return;
    //         }

    //         const { source, id } = context;

    //         if (source === 'navbar_click' && id && options.onNavbarClick) {
    //             try {
    //                 await options.onNavbarClick(id);
    //             } finally {
    //                 removeNavigationContext();
    //             }
    //         }
    //     };

    //     run();

    //     return () => {
    //         mounted = false;
    //     };
    // }, [channel, options.onNavbarClick]);

    useEffect(() => {}, []);
};

export { useWebviewReactivated };
